import { PERSONAL_PAGE_SUBTYPE_LABELS } from '../../labels';
import { cap } from '../capability-builder';
import { registrantLabels, registrantOptions, ticketLabels, ticketOptions } from '../item-presets';
import type { DefineStepAccordionSectionId, PersonalPageCapabilities } from '../types';

export const EVENT_DEFINE_STEP_ACCORDION_ORDER: DefineStepAccordionSectionId[] = [
  'units',
  'advancedTickets',
  'donorForm',
  'payment',
  'schedule',
  'shareMeta'
];

export const eventCapabilities: Record<string, PersonalPageCapabilities> = {
  ticketSales: cap({
    label: PERSONAL_PAGE_SUBTYPE_LABELS.ticketSales.singular,
    hasLocation: true,
    hasDateTime: true,
    requiresDefinedItems: true,
    itemKind: 'ticket',
    itemOptions: ticketOptions,
    dataDestination: 'registrations',
    itemLabels: ticketLabels,
    defineStepAccordionOrder: EVENT_DEFINE_STEP_ACCORDION_ORDER,
    defineStepLabelConfig: {
      formSubject: 'רכישת כרטיסים',
      overrides: {
        registrationLimitTitle: 'מכסה כוללת לדף',
        registrationLimitSubtitle: 'הגבלת סך הכרטיסים בדף (בנוסף למלאי לכל סוג כרטיס)',
        perBuyerLimitTitle: 'הגבלת רכישה לסוג כרטיס זה (לפי אדם)',
        perBuyerLimitMaxLabel: 'מקסימום כרטיסים מסוג זה לאדם:',
        perBuyerLimitHint:
          'חל רק על סוג הכרטיס שעורכים. ההגבלה מחושבת לפי מספר טלפון. הגבלה על כל הדף מוגדרת בשלב «הגדרות ברמת הדף».',
        accordion: {
          unitsCardTitle: 'סוגי כרטיסים',
          unitsCardTitleWithInventory: 'סוגי כרטיסים',
          unitsCardDescription: 'הגדירו סוגי כרטיסים: שם, מחיר ומלאי.',
          unitsCardDescriptionWithInventory: 'הגדירו סוגי כרטיסים: שם, מחיר ומלאי.',
          advancedTicketsCardTitle: 'הגדרות ברמת הדף',
          advancedTicketsCardDescription:
            'מכסה כוללת, מכסה לפי אדם וקופונים — כללי לדף או פרטי לסוג כרטיס.',
          advancedTicketsCouponsHint:
            'בחרו היקף כללי או פרטי למכסה לפי אדם ולקופונים. המכסה לפי אדם מחושבת לפי מספר טלפון.',
          pagePerBuyerLimitTitle: 'מכסה לפי אדם — כללית לדף',
          pagePerBuyerLimitMaxLabel: 'מקסימום כרטיסים לאדם בכל הדף:',
          pagePerBuyerLimitHint:
            'מגבילה את סך הרכישות מכל סוגי הכרטיסים יחד לכל מספר טלפון — בנפרד מהגבלה לסוג כרטיס.',
          formCardTitle: 'פרטי טופס הרישום',
          formCardDescription: 'סמנו אילו שדות יהיו חובה או רשות, והוסיפו שדות חופשיים לפי הצורך.',
          paymentCardTitle: 'הגדרות תשלום',
          eventPaymentModeLabel: 'אופן תשלום עבור הלקוח בדף הציבורי',
          scheduleCardTitle: 'זמני הפעלה',
          scheduleCardDescriptionWithRange: 'ממתי עד מתי הדף פתוח להרשמה (אופציונלי).',
          scheduleCardDescriptionNameOnly: 'שם הדף.',
          shareCardTitle: 'כותרת ותמונה לשיתוף בוואטסאפ וברשתות',
          shareCardDescription: 'הכותרת, התיאור והתמונה שיוצגו לרוכשים בשיתוף.'
        }
      }
    },
    defineStepChoice: {
      label: 'מכירת כרטיסים',
      helpText: 'הגדרת סוגי כרטיסים בתשלום עם מלאי וקופונים.',
      icon: 'confirmation'
    }
  }),

  freeEventRegistration: cap({
    label: PERSONAL_PAGE_SUBTYPE_LABELS.freeEventRegistration.singular,
    hasLocation: true,
    hasDateTime: true,
    itemKind: 'registrantType',
    itemOptions: registrantOptions,
    dataDestination: 'registrations',
    itemLabels: registrantLabels,
    defineStepAccordionOrder: EVENT_DEFINE_STEP_ACCORDION_ORDER,
    defineStepLabelConfig: {
      formSubject: 'הרשמה',
      overrides: {
        registrationLimitTitle: 'מספר הרשמות כולל',
        registrationLimitSubtitle: 'הגבלת סך הנרשמים בדף (בנוסף למלאי לכל סוג נרשם)',
        perBuyerLimitTitle: 'הגבלת הרשמה לסוג נרשם זה (לפי אדם)',
        perBuyerLimitMaxLabel: 'מקסימום מקומות מסוג זה לאדם:',
        perBuyerLimitHint:
          'חל רק על סוג הנרשם שעורכים. ההגבלה מחושבת לפי מספר טלפון. הגבלה על כל הדף מוגדרת בשלב «הגדרות ברמת הדף».',
        accordion: {
          unitsCardTitle: 'סוגי נרשמים',
          unitsCardTitleWithInventory: 'סוגי נרשמים',
          unitsCardDescription: 'הגדירו סוגי נרשמים עם מלאי.',
          unitsCardDescriptionWithInventory: 'הגדירו סוגי נרשמים עם מלאי.',
          advancedTicketsCardTitle: 'הגדרות ברמת הדף',
          advancedTicketsCardDescription: 'מכסה כוללת ומכסה לפי אדם — כללית לדף או פרטית לסוג נרשם.',
          advancedTicketsCouponsHint: 'בחרו היקף כללי או פרטי למכסה לפי אדם. המכסה מחושבת לפי מספר טלפון.',
          pagePerBuyerLimitTitle: 'מכסה לפי אדם — כללית לדף',
          pagePerBuyerLimitMaxLabel: 'מקסימום הרשמות לאדם בכל הדף:',
          pagePerBuyerLimitHint:
            'מגבילה את סך ההרשמות מכל הסוגים יחד לכל מספר טלפון — בנפרד מהגבלה לסוג נרשם.',
          formCardTitle: 'פרטי טופס הרישום',
          formCardDescription: 'סמנו אילו שדות יהיו חובה או רשות, והוסיפו שדות חופשיים לפי הצורך.',
          scheduleCardTitle: 'זמני הפעלה',
          scheduleCardDescriptionWithRange: 'ממתי עד מתי הדף פתוח להרשמה (אופציונלי).',
          scheduleCardDescriptionNameOnly: 'שם הדף.',
          shareCardTitle: 'כותרת ותמונה לשיתוף בוואטסאפ וברשתות',
          shareCardDescription: 'הכותרת, התיאור והתמונה שיוצגו לנרשמים בשיתוף.'
        }
      }
    },
    defineStepChoice: {
      label: 'הרשמה חינמית',
      helpText: 'הרשמה לאירוע ללא תשלום, עם אפשרות להגביל מספר נרשמים.',
      icon: 'registration'
    }
  }),

  choiceEvent: cap({
    label: PERSONAL_PAGE_SUBTYPE_LABELS.choiceEvent.singular,
    hasLocation: true,
    hasDateTime: true,
    allowSubtypeChoice: true,
    subtypeChoiceOptions: ['ticketSales', 'freeEventRegistration'],
    dataDestination: 'registrations',
    itemLabels: ticketLabels,
    defineStepAccordionOrder: EVENT_DEFINE_STEP_ACCORDION_ORDER,
    defineStepLabelConfig: {
      formSubject: 'הרשמה',
      overrides: {
        choiceSectionTitle: 'בחר צורת הרשמה',
        accordion: {
          unitsCardTitle: 'בחירת סוג ההרשמה',
          unitsCardDescription: 'בחרו בין מכירת כרטיסים להרשמה חינמית.'
        }
      }
    }
  })
};
